import { Component } from '@theme/component'
import { useGsap } from '@theme/gsap'
import { mediaQueryLarge } from '@theme/use-device-detection'
import { usePointerInertia } from '@theme/use-pointer-inertia'

const inertiaElementSelector = '[data-inertia-element]'
const cardSelector = '.work-card'
const videoSelector = '[data-work-card-video]'
const soundSelector = '[data-work-card-sound]'
const playPauseSelector = '[data-work-card-playpause]'
const noPreference = '(prefers-reduced-motion: no-preference)'

class WorkGrid extends Component {
  /** @type {{ revert: () => void } | null} */
  #gsapContext = null

  /** @type {(() => void) | null} */
  #teardownInertia = null

  /** @type {HTMLVideoElement[]} */
  #videos = []

  /** @type {Array<() => void>} */
  #videoCleanup = []

  /** @type {WeakSet<HTMLVideoElement>} */
  #manuallyPaused = new WeakSet()

  /** @type {WeakSet<HTMLVideoElement>} */
  #manuallyPlaying = new WeakSet()

  mounted() {
    void this.#initInertia()
    this.#initVideos()
  }

  unmounted() {
    this.#gsapContext?.revert()
    this.#gsapContext = null
    this.#teardownInertia?.()
    this.#teardownInertia = null
    this.removeEventListener('click', this.#handleClick)
    for (const dispose of this.#videoCleanup) dispose()
    this.#videoCleanup = []
    this.#videos = []
  }

  #initVideos() {
    this.#videos = Array.from(this.querySelectorAll(videoSelector))
    if (!this.#videos.length) return

    for (const video of this.#videos) {
      video.muted = true
      video.loop = true
      video.playsInline = true

      const onPlay = () => {
        this.#manuallyPaused.delete(video)
        this.#playPauseButtonFor(video)?.classList.add('is-secondary-icon')
      }
      const onPause = () =>
        this.#playPauseButtonFor(video)?.classList.remove('is-secondary-icon')
      const onVolume = () =>
        this.#soundButtonFor(video)?.classList.toggle(
          'is-secondary-icon',
          video.muted
        )

      video.addEventListener('play', onPlay)
      video.addEventListener('pause', onPause)
      video.addEventListener('volumechange', onVolume)

      const card = video.closest(cardSelector)
      const onEnter = () => {
        if (!this.#manuallyPaused.has(video)) this.#safePlay(video)
      }
      const onLeave = () => {
        if (!this.#manuallyPlaying.has(video)) video.pause()
      }

      card?.addEventListener('mouseenter', onEnter)
      card?.addEventListener('mouseleave', onLeave)

      this.#videoCleanup.push(() => {
        card?.removeEventListener('mouseenter', onEnter)
        card?.removeEventListener('mouseleave', onLeave)
        video.removeEventListener('play', onPlay)
        video.removeEventListener('pause', onPause)
        video.removeEventListener('volumechange', onVolume)
      })
    }

    this.addEventListener('click', this.#handleClick)
  }

  #handleClick = (event) => {
    const target = /** @type {Element} */ (event.target)
    const soundButton = target.closest?.(soundSelector)
    if (soundButton) {
      event.preventDefault()
      this.#toggleSound(this.#videoForControl(soundButton))
      return
    }

    const playPauseButton = target.closest?.(playPauseSelector)
    if (playPauseButton) {
      event.preventDefault()
      this.#togglePlayPause(this.#videoForControl(playPauseButton))
      return
    }

    const card = target.closest?.('.work-card')
    if (card) this.#togglePlayPause(card.querySelector(videoSelector))
  }

  /** @param {HTMLVideoElement | null} video */
  #toggleSound(video) {
    if (!video) return

    if (video.muted) {
      for (const other of this.#videos) {
        if (other !== video) other.muted = true
      }
      video.muted = false
      video.volume = 1
      this.#manuallyPlaying.add(video)
      this.#safePlay(video)
    } else {
      video.muted = true
      this.#manuallyPlaying.delete(video)
    }
  }

  /** @param {HTMLVideoElement | null} video */
  #togglePlayPause(video) {
    if (!video) return

    if (video.paused) {
      this.#manuallyPaused.delete(video)
      this.#manuallyPlaying.add(video)
      this.#safePlay(video)
    } else {
      this.#manuallyPlaying.delete(video)
      this.#manuallyPaused.add(video)
      video.pause()
    }
  }

  /** @param {HTMLVideoElement} video */
  #safePlay(video) {
    const played = video.play()
    if (played instanceof Promise) played.catch(() => undefined)
  }

  /** @param {Element} control */
  #videoForControl(control) {
    return control.closest('.work-card')?.querySelector(videoSelector) ?? null
  }

  /** @param {HTMLVideoElement} video */
  #soundButtonFor(video) {
    return video.closest('.work-card')?.querySelector(soundSelector) ?? null
  }

  /** @param {HTMLVideoElement} video */
  #playPauseButtonFor(video) {
    return video.closest('.work-card')?.querySelector(playPauseSelector) ?? null
  }

  async #initInertia() {
    if (!(mediaQueryLarge.matches && matchMedia(noPreference).matches)) return

    const scope = await useGsap(this, undefined, ['InertiaPlugin'])

    if (!(scope && this.isConnected && globalThis.InertiaPlugin)) {
      scope?.context.revert()
      return
    }

    scope.contextSafe(() => {
      const mm = scope.gsap.matchMedia(this)
      mm.add(
        { desktop: mediaQueryLarge.media, motion: noPreference },
        (context) => {
          if (!(context.conditions.desktop && context.conditions.motion)) return
          this.#teardownInertia = usePointerInertia(
            scope.gsap,
            this.querySelectorAll(inertiaElementSelector)
          )
        }
      )
    })

    this.#gsapContext = scope.context
  }
}

if (!customElements.get('work-grid')) {
  customElements.define('work-grid', WorkGrid)
}
